// Single source of truth for "where does this role live?".
//
// Two vocabularies exist for the same role: the API returns sUserRole capitalised
// ('User' | 'Admin' | 'Technical'), while localStorage stores aviagen_userRole
// lowercased ('user' | 'admin' | 'technical'). Comparing across the two without
// normalising is what previously caused guards to disagree and redirect at each other.
//
// Keep the keys in step with the server-side role enum (sUserRole in the API's
// src/models/user.model.js) and the values in step with the layout route tables in
// routes.js. If a value stops matching a registered route, that layout's catch-all
// becomes self-referential and the redirect loop returns.
//
// Null prototype is deliberate: a plain object literal resolves '__proto__' and
// 'constructor' to inherited members, which are truthy, so an unknown role would
// satisfy the `!roleHome(role)` guards and hand a non-path to <Navigate>. The role is
// read from user-editable localStorage, so it cannot be assumed to be one of the three.
export const ROLE_HOME = Object.assign(Object.create(null), {
    admin: '/admin/documentmanagement',
    technical: '/technicalteam/documentmanagement',
    user: '/user/documents'
});

// Returns null - deliberately not '/' - when the role is unknown. Collapsing
// "role not known yet" into "go to the login screen" is the redirect-loop bug;
// callers must decide what an unknown role means for them.
export const roleHome = (role) => ROLE_HOME[String(role || '').toLowerCase()] || null;

// Where a signed-in visitor to the login route ('/') should be sent; null means
// "show the login form". Requires BOTH a token and a resolvable role.
export const loginRedirect = (hasToken, storedRole) => (
    hasToken ? roleHome(storedRole) : null
);

// The role for a layout guard, preferring redux (which comes from the API) and
// falling back to localStorage. Sessions from the previous build have an emptied
// userinfo persisted alongside a live token, and "role not in redux" is not the
// same thing as "not signed in".
export const resolveHome = (reduxRole, storedRole) => (
    roleHome(reduxRole) || roleHome(storedRole)
);

// Where a layout guard should send this visitor; null means "render the layout".
//
// Loop-freedom: this returns '/' only when there is no token or no resolvable role,
// and loginRedirect() returns a destination only when BOTH exist. The two conditions
// are mutually exclusive, so '/' and a layout can never redirect at each other. Any
// change to either function must preserve that - see roleRoutes.test.js.
export const guardRedirect = (hasToken, reduxRole, storedRole, layoutHome) => {
    const home = resolveHome(reduxRole, storedRole);
    if (!hasToken || !home) {
        return '/';
    }
    return home === layoutHome ? null : home;
};
